import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, EyeIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, CalendarIcon, DownloadIcon, UsersIcon, ShieldIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, ClipboardListIcon, ChevronDownIcon, ChevronUpIcon, StethoscopeIcon, ActivityIcon, PillIcon, LayersIcon, TrashIcon, MinusCircleIcon, PlusCircleIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../contexts/ToastContext';
import { Patient, Gender } from '../../types';
import { formatDate, formatTime, getGenderName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { validatePatientForm } from '../../utils/validation';
import { exportPatientsToExcel } from '../../utils/excelExport';
import { exportPatientsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Patients({
  autoOpenNew
}: {
  autoOpenNew?: boolean;
}) {
  const {
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    insurances,
    getInsurance,
    getAppointmentsByPatient,
    getDoctor,
    getService,
    services,
    getSpecialty,
    doctors,
    appointments,
    getClinicalHistoriesByPatient,
    addSessionPackage,
    updateSessionPackage,
    archiveSessionPackage,
    unarchiveSessionPackage,
    registerSessionUsage,
    undoSessionUsage
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly,
    canView,
    permissions
  } = usePermissions();
  const readOnly = isReadOnly('patients');
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // Check if user can see insurance field
  const canSeeInsurance = canView('patients_insurance');
  // Find the doctor record that matches the current user (by email)
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctorUser = !!currentUserDoctor && !isAdmin;
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Session Packages state
  const [isPackageFormOpen, setIsPackageFormOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [packageFormData, setPackageFormData] = useState({
    serviceId: '',
    doctorId: '',
    totalSessions: 6,
    notes: ''
  });
  const [packageFormErrors, setPackageFormErrors] = useState<Record<string, string>>({});
  const [isArchivePackageConfirmOpen, setIsArchivePackageConfirmOpen] = useState(false);
  const [packageToArchive, setPackageToArchive] = useState<string | null>(null);
  const [expandedPackageLogId, setExpandedPackageLogId] = useState<string | null>(null);
  // Expanded clinical history entries
  const [expandedVisitId, setExpandedVisitId] = useState<string | null>(null);
  // Clinical history statuses (read from same localStorage as ClinicalHistory module)
  const CH_STATUS_KEY = 'savidna-clinical-history-status';
  const [chStatuses, setChStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  // Listen for storage changes (in case ClinicalHistory module updates statuses)
  useEffect(() => {
    const handleStorage = () => {
      try {
        const saved = localStorage.getItem(CH_STATUS_KEY);
        if (saved) setChStatuses(JSON.parse(saved));
      } catch {}
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const getClinicalHistoryStatus = (id: string): 'active' | 'archived' | 'deactivated' => chStatuses[id] || 'active';
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-patients-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    // Persist to Supabase
    updatePatient(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    // Persist to Supabase
    updatePatient(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring patient in Supabase:', e));
    setIsActionConfirmOpen(false);
    setIsViewOpen(false);
  };
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    gender: 'masculino' as Gender,
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    insuranceId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filter patients: doctors only see patients they have appointments with
  const accessiblePatients = isDoctorUser && currentUserDoctor ? patients.filter(p => appointments.some(a => a.patientId === p.id && a.doctorId === currentUserDoctor.id)) : patients;
  const filteredPatients = accessiblePatients.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.email?.toLowerCase().includes(searchTerm.toLowerCase()) || p.phone?.includes(searchTerm) || p.cedula && p.cedula.includes(searchTerm);
    const status = getItemStatus(p.id);
    return matchesSearch && status === viewMode;
  });
  const {
    sortedItems: sortedPatients,
    requestSort,
    isSorted
  } = useSortableData(filteredPatients);
  const {
    paginatedItems: paginatedPatients,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedPatients);
  // Counts for tabs
  const activeCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'active').length;
  const archivedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'archived').length;
  const deactivatedCount = accessiblePatients.filter(p => getItemStatus(p.id) === 'deactivated').length;
  const genderOptions = [{
    value: 'masculino',
    label: 'Masculino'
  }, {
    value: 'femenino',
    label: 'Femenino'
  }, {
    value: 'otro',
    label: 'Otro'
  }];
  const insuranceOptions = [{
    value: '',
    label: 'Sin seguro'
  }, ...insurances.map(i => ({
    value: i.id,
    label: i.name
  }))];
  // Format Cédula as user types
  const handleCedulaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 11) value = value.slice(0, 11); // Limit to 11 digits
    // Format: 000-0000000-0
    let formatted = value;
    if (value.length > 3) {
      formatted = value.slice(0, 3) + '-' + value.slice(3);
    }
    if (value.length > 10) {
      formatted = formatted.slice(0, 11) + '-' + formatted.slice(11);
    }
    setFormData(prev => ({
      ...prev,
      cedula: formatted
    }));
  };
  const openNewForm = () => {
    if (!canCreate('patients')) return;
    setSelectedPatient(null);
    setFormData({
      name: '',
      gender: 'masculino',
      phone: '',
      email: '',
      address: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      insuranceId: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Auto-open new patient form when navigated with :new param
  // setTimeout ensures the module is fully mounted before opening the modal
  useEffect(() => {
    if (autoOpenNew && permissions) {
      const timer = setTimeout(() => openNewForm(), 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew, permissions]);
  const openEditForm = (patient: Patient) => {
    if (!canEdit('patients')) return;
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      gender: patient.gender,
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      emergencyContactName: patient.emergencyContact?.name || '',
      emergencyContactRelationship: patient.emergencyContact?.relationship || '',
      emergencyContactPhone: patient.emergencyContact?.phone || '',
      insuranceId: patient.insuranceId || ''
    });
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (patient: Patient) => {
    setSelectedPatient(patient);
    // Re-read clinical history statuses when opening view
    try {
      const saved = localStorage.getItem(CH_STATUS_KEY);
      if (saved) setChStatuses(JSON.parse(saved));
    } catch {}
    setActiveTab('info');
    setIsViewOpen(true);
  };
  // ===== SESSION PACKAGES HANDLERS =====
  const activeServiceOptions = services.map(s => ({
    value: s.id,
    label: s.name
  }));
  const doctorOptionsForPackages = doctors.map(d => ({
    value: d.id,
    label: d.name
  }));
  const openNewPackageForm = () => {
    setEditingPackageId(null);
    setPackageFormData({
      serviceId: '',
      doctorId: isDoctorUser && currentUserDoctor ? currentUserDoctor.id : '',
      totalSessions: 6,
      notes: ''
    });
    setPackageFormErrors({});
    setIsPackageFormOpen(true);
  };
  const openEditPackageForm = (pkg: NonNullable<Patient['sessionPackages']>[number]) => {
    setEditingPackageId(pkg.id);
    setPackageFormData({
      serviceId: pkg.serviceId,
      doctorId: pkg.doctorId || '',
      totalSessions: pkg.totalSessions,
      notes: pkg.notes || ''
    });
    setPackageFormErrors({});
    setIsPackageFormOpen(true);
  };
  const handlePackageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) return;
    const errors: Record<string, string> = {};
    if (!editingPackageId && !packageFormData.serviceId) {
      errors.serviceId = 'Selecciona un servicio';
    }
    if (!packageFormData.totalSessions || packageFormData.totalSessions < 1) {
      errors.totalSessions = 'Ingresa una cantidad válida de sesiones';
    }
    if (Object.keys(errors).length > 0) {
      setPackageFormErrors(errors);
      return;
    }
    try {
      if (editingPackageId) {
        await updateSessionPackage(selectedPatient.id, editingPackageId, {
          totalSessions: packageFormData.totalSessions,
          notes: packageFormData.notes,
          doctorId: packageFormData.doctorId || undefined
        });
        showToast({
          type: 'success',
          title: 'Paquete actualizado',
          message: 'Los cambios fueron guardados y registrados en la bitácora.'
        });
      } else {
        await addSessionPackage(selectedPatient.id, {
          serviceId: packageFormData.serviceId,
          doctorId: packageFormData.doctorId || undefined,
          totalSessions: packageFormData.totalSessions,
          notes: packageFormData.notes
        });
        showToast({
          type: 'success',
          title: 'Paquete de sesiones creado',
          message: 'El servicio fue asignado al paciente.'
        });
      }
      // Refresh selectedPatient with latest data
      const refreshed = getPatient(selectedPatient.id);
      if (refreshed) setSelectedPatient(refreshed);
      setIsPackageFormOpen(false);
    } catch (err) {
      logger.error('Error guardando paquete de sesiones:', err);
      showToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo guardar el paquete de sesiones.'
      });
    }
  };
  const handleRegisterSession = async (packageId: string) => {
    if (!selectedPatient) return;
    try {
      await registerSessionUsage(selectedPatient.id, packageId);
      const refreshed = getPatient(selectedPatient.id);
      if (refreshed) setSelectedPatient(refreshed);
      showToast({
        type: 'success',
        title: 'Sesión registrada',
        message: 'Se sumó 1 sesión al paquete.'
      });
    } catch (err) {
      logger.error('Error registrando sesión:', err);
    }
  };
  const handleUndoSession = async (packageId: string, logEntryId: string) => {
    if (!selectedPatient) return;
    try {
      await undoSessionUsage(selectedPatient.id, packageId, logEntryId);
      const refreshed = getPatient(selectedPatient.id);
      if (refreshed) setSelectedPatient(refreshed);
    } catch (err) {
      logger.error('Error revirtiendo sesión:', err);
    }
  };
  const confirmArchivePackage = (packageId: string) => {
    confirm({
      title: 'Archivar Paquete de Sesiones',
      message: '¿Deseas archivar este paquete de sesiones? Dejará de aparecer como activo, pero su historial se conserva y puede restaurarse cuando quieras.',
      confirmText: 'Archivar',
      variant: 'warning',
      onConfirm: async () => {
        if (!selectedPatient) return;
        try {
          await archiveSessionPackage(selectedPatient.id, packageId);
          const refreshed = getPatient(selectedPatient.id);
          if (refreshed) setSelectedPatient(refreshed);
          showToast({
            type: 'success',
            title: 'Paquete archivado',
            message: 'El paquete fue archivado. Su historial se conserva.'
          });
        } catch (err) {
          logger.error('Error archivando paquete:', err);
        }
      }
    });
  };
  const handleUnarchivePackage = async (packageId: string) => {
    if (!selectedPatient) return;
    try {
      await unarchiveSessionPackage(selectedPatient.id, packageId);
      const refreshed = getPatient(selectedPatient.id);
      if (refreshed) setSelectedPatient(refreshed);
      showToast({
        type: 'success',
        title: 'Paquete restaurado',
        message: 'El paquete fue restaurado desde archivados.'
      });
    } catch (err) {
      logger.error('Error restaurando paquete:', err);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePatientForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const patientData = {
      name: capitalizeName(formData.name),
      gender: formData.gender,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      emergencyContact: {
        name: capitalizeName(formData.emergencyContactName),
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      },
      insuranceId: formData.insuranceId || null
    };
    confirm({
      title: selectedPatient ? 'Guardar Cambios' : 'Crear Paciente',
      message: selectedPatient ? '¿Estás seguro de que deseas guardar los cambios de este paciente?' : '¿Estás seguro de que deseas registrar a este nuevo paciente?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          if (selectedPatient) {
            await updatePatient(selectedPatient.id, patientData);
            showToast({
              type: 'success',
              title: 'Paciente actualizado',
              message: `${patientData.name} fue actualizado correctamente.`
            });
          } else {
            await addPatient(patientData);
            showToast({
              type: 'success',
              title: 'Paciente creado',
              message: `${patientData.name} fue registrado correctamente.`
            });
          }
          setIsFormOpen(false);
        } catch (error: any) {
          logger.error('Error guardando paciente:', error);
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: error?.message || 'No se pudo guardar el paciente.'
          });
          setFormErrors({
            general: error?.message || 'Error al guardar el paciente'
          });
        }
      }
    });
  };
  const openActionConfirm = (patient: Patient, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedPatient(patient);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Paciente';
      message = `¿Estás seguro de que deseas archivar a "${patient.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Paciente';
      message = `¿Estás seguro de que deseas desactivar a "${patient.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Paciente';
      message = `¿Estás seguro de que deseas eliminar permanentemente a "${patient.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          archiveItem(patient.id);
        } else if (type === 'deactivate') {
          deactivateItem(patient.id);
        } else if (type === 'delete') {
          deletePatient(patient.id);
          setIsViewOpen(false);
        }
      }
    });
  };
  const saveMedicalHistory = async () => {
    if (readOnly) return;
    if (selectedPatient && medicalData) {
      setIsSavingMedical(true);
      setMedicalSaveError('');
      setMedicalSaveSuccess(false);
      try {
        await updateMedicalHistory(selectedPatient.id, medicalData);
        setMedicalSaveSuccess(true);
        setTimeout(() => setMedicalSaveSuccess(false), 3000);
      } catch (error: any) {
        logger.error('❌ Error guardando historial médico:', error);
        setMedicalSaveError(error?.message || 'Error al guardar el historial médico');
      } finally {
        setIsSavingMedical(false);
      }
    }
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-4452" data-mp="xlipf2rj7yl3" />
  }, {
    id: 'sessions',
    label: 'Sesiones',
    icon: <LayersIcon className="w-4 h-4" data-id="element-4453" data-mp="12i95vr790w71" />
  }, {
    id: 'visits',
    label: 'Registro de Visitas',
    icon: <ClipboardListIcon className="w-4 h-4" data-id="element-4454" data-mp="ojj7m88p0qh3" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-4455" data-mp="sn8samm8ynwu" />
  }];
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToExcel(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = patients;
    if (dateFrom && dateTo) {
      dataToExport = patients.filter(p => {
        const dateStr = new Date(p.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportPatientsToPdf(dataToExport, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // Helper to capitalize each word in a name
  const capitalizeName = (name: string) => {
    return name.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="space-y-6" data-id="element-4456" data-mp="2a3oi72s9dx46">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-4457" data-mp="2f29kd7r2mqv9">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(ellipse at center, transparent 30%, rgba(255,255,255,1) 31%, rgba(255,255,255,1) 32%, transparent 33%), radial-gradient(ellipse at center, transparent 60%, rgba(255,255,255,1) 61%, rgba(255,255,255,1) 62%, transparent 63%)',
        backgroundSize: '48px 48px'
      }} data-id="element-4458" data-mp="2jz01lyem3si9" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4459" data-mp="1ha1rf8k88sp4" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4460" data-mp="1r64seb3celah" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-4461" data-mp="1m7ti5z94y8rq" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-4462" data-mp="1vzhh0l80us38" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-4463" data-mp="2okw9bqi28is6" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-4464" data-mp="2tj7jk2c9ovax">
          <div data-id="element-4465" data-mp="2el5f7pxew5sm">
            <div className="flex items-center gap-3 mb-2" data-id="element-4466" data-mp="2enrobtzyatcy">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4467" data-mp="2jjqijy7yn5la">
                <UsersIcon className="w-6 h-6 text-white" data-id="element-4468" data-mp="25sbwfiiraoa0" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-4469" data-mp="2aklmqzns9qct">Pacientes</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-4470" data-mp="1vvfkf2tnfk0p">
              {isDoctorUser ? 'Mis pacientes — Solo se muestran pacientes con citas asignadas a usted' : 'Gestión de pacientes y expedientes médicos'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-4471" data-mp="1qw2zwoq92p1a">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4472" data-mp="3tkkw3ua4dkn1">
            {canExportPdf && <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-4474" data-mp="3jtzudxz5fzzw" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4473" data-mp="3opygfex095x5">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4476" data-mp="6cqv5xq44u3m" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-4475" data-mp="bb24868l30ks">
                Exportar Excel
              </Button>}
            {canCreate('patients') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4478" data-mp="12l6on5az576v" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-4477" data-mp="2n4s82nqphmux">
                Nuevo Paciente
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-4479" data-mp="1gdmjvjxmw0x8">
        <div className="w-full md:w-96 relative" data-id="element-4480" data-mp="1la39kikhencf">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-4481" data-mp="1ldh8fgtxsef4" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar por nombre, cédula, correo o teléfono..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-4482" data-mp="1uc2sjgf241i0" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-4483" data-mp="26u6psq0e0ey1">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-4484" data-mp="1xrnrmj7jqqi4">
              <tab.icon className="w-4 h-4" data-id="element-4485" data-mp="v0wdwmxuhml7" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-4486" data-mp="uz1sl7invsgl">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Patients List */}
      {/* ── MOBILE CARD LIST ── */}
      <div className="sm:hidden space-y-0 divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden" data-id="element-4487" data-mp="19rc0a51zz0ky">
        {paginatedPatients.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-4488" data-mp="zyeloa04lvrm">
            <UsersIcon className="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" data-id="element-4489" data-mp="14x7rrhd32khr" />
            <p className="text-sm" data-id="element-4490" data-mp="1snlo3k2pb5i5">
              {searchTerm ? 'No hay resultados' : 'No hay pacientes'}
            </p>
          </div> : paginatedPatients.map(patient => <div key={patient.id} className="px-4 py-3 flex items-center gap-3" data-id="element-4491" data-mp="2zfds97kx24z0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-4492" data-mp="34dfiqdko96ki">
                {patient.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0" data-id="element-4493" data-mp="2ggrrxux84hw5">
                <p className="font-semibold text-gray-900 dark:text-white text-sm truncate capitalize" data-id="element-4494" data-mp="2gi32qep36pwr">
                  {patient.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4495" data-mp="2phpu07o3qfc3">
                  {patient.phone} · {patient.email}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4496" data-mp="2uegdl4z47t29">
                <button onClick={() => openView(patient)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver" data-id="element-4497" data-mp="4qpei3p921wi">
                  <EyeIcon className="w-4 h-4" data-id="element-4498" data-mp="eunqbe9ts0hu" />
                </button>
                {viewMode === 'active' && canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4499" data-mp="a2w41fxpcgxi">
                    <EditIcon className="w-4 h-4" data-id="element-4500" data-mp="3s9akpmncv4zl" />
                  </button>}
              </div>
            </div>)}
        {/* Mobile pagination */}
        <div className="px-4 pb-3 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-4501" data-mp="3ja5j27ghyvj7">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-4502" data-mp="1lk4as7vtm94u" />
        </div>
      </div>

      <div className="hidden sm:block" data-id="element-4503" data-mp="3ft13bcyfvtgs">
        <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-4504" data-mp="3201qjiq1kdcx">
          <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-4505" data-mp="31xyq04r9j1ty" />
          <div className="overflow-x-auto" data-id="element-4506" data-mp="362g5hrqlasyx">
            <table className="w-full min-w-[600px]" data-id="element-4507" data-mp="3tr7t05t8in5d">
              <thead data-id="element-4508" data-mp="3trr0stp67v5o">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-4509" data-mp="3tp4sgrughbme">
                  <SortableHeader label="Paciente" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-4510" data-mp="2ik5bj7iunun" />
                  <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-4511" data-mp="29aut6n2bptt9" />
                  <SortableHeader label="Dirección" sortKey="address" currentDirection={isSorted('address')} onSort={requestSort} className="hidden sm:table-cell" data-id="element-4512" data-mp="25sf2n8sekjq8" />
                  <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4513" data-mp="2as9vviznhleg">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4514" data-mp="1i5gsesuvm80r">
                {filteredPatients.length === 0 ? <tr data-id="element-4515" data-mp="1wul8o4i42268">
                    <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-4516" data-mp="1s1jujypqa6np">
                      <div className="flex flex-col items-center justify-center" data-id="element-4517" data-mp="14a4mrqf6mx52">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-4518" data-mp="zd4hviu2k0fj">
                          <UsersIcon className="w-8 h-8 text-gray-400" data-id="element-4519" data-mp="7l3xswrv9wyj" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-4520" data-mp="bmxsmofds6qs">
                          No se encontraron pacientes
                        </h3>
                        <p className="text-sm" data-id="element-4521" data-mp="gf8sxse19ujv">
                          {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo paciente' : 'No hay pacientes en esta vista'}
                        </p>
                      </div>
                    </td>
                  </tr> : paginatedPatients.map(patient => {
                return <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-4522" data-mp="314odi5mnsn12">
                        <td className="py-4 px-6" data-id="element-4523" data-mp="2osckox33av9j">
                          <div className="flex items-center gap-3" data-id="element-4524" data-mp="2orknjhtugph5">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold shadow-sm" data-id="element-4525" data-mp="2juu72te4nrv7">
                              {patient.name.charAt(0).toUpperCase()}
                            </div>
                            <div data-id="element-4526" data-mp="37kydt9mm47qf">
                              <p className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-4527" data-mp="37hsmdnqigtrj">
                                {patient.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4528" data-mp="2yip1ce7sa4ae">
                                {getGenderName(patient.gender)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6" data-id="element-4529" data-mp="1om8sebdf0594">
                          <div className="space-y-1" data-id="element-4530" data-mp="1oje9wpdyouic">
                            <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1.5" data-id="element-4531" data-mp="1tiz8gyp7z0uj">
                              <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-4532" data-mp="1fjzhvli5cbvq" />{' '}
                              {patient.phone}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5" data-id="element-4533" data-mp="15u7p8wtlxstw">
                              <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-4534" data-mp="1amhjuq5o3h2i" />{' '}
                              {patient.email}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell" data-id="element-4535" data-mp="10w62o1wz3bdo">
                          {patient.address || '-'}
                        </td>
                        <td className="py-4 px-6" data-id="element-4536" data-mp="rtn59xbyhqyt">
                          <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-4537" data-mp="88wmpaka3egy">
                            <button onClick={() => openView(patient)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-4538" data-mp="3bmob5ewvsgz">
                              <EyeIcon className="w-4 h-4" data-id="element-4539" data-mp="r5o57hrzxpjy" />
                            </button>

                            {viewMode === 'active' ? <>
                                {canEdit('patients') && <button onClick={() => openEditForm(patient)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-4540" data-mp="vzsdq5gwu5n5">
                                    <EditIcon className="w-4 h-4" data-id="element-4541" data-mp="2hapgjqf2alg0" />
                                  </button>}
                                {canDelete('patients') && <>
                                    <button onClick={() => openActionConfirm(patient, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-4542" data-mp="2qacn0qmnehfx">
                                      <ArchiveIcon className="w-4 h-4" data-id="element-4543" data-mp="19y5vp0hz7m3b" />
                                    </button>
                                    <button onClick={() => openActionConfirm(patient, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-4544" data-mp="1evfkcdxjjtq3">
                                      <PowerOffIcon className="w-4 h-4" data-id="element-4545" data-mp="oeltvzsii0qz" />
                                    </button>
                                  </>}
                              </> : <button onClick={() => restoreItem(patient.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-4546" data-mp="eok3h1cos68w">
                                <RotateCcwIcon className="w-4 h-4" data-id="element-4547" data-mp="1tm02ihhg7xhg" />
                              </button>}
                          </div>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-4548" data-mp="1frz1fh30xydx">
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-4549" data-mp="2cjjadhs67a2a" />
          </div>
        </Card>

        {/* Patient Form Modal */}
      </div>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPatient ? 'Editar Paciente' : 'Nuevo Paciente'} size="lg" icon={<UsersIcon className="w-5 h-5" data-id="element-4551" data-mp="d30ur17ulgpj" />} accentColor="emerald" data-id="element-4550" data-mp="s2eg4rjeep1h">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4552" data-mp="i1lrurrszk9r">
          {/* General Error Message */}
          {formErrors.general && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-4553" data-mp="mxkg8fd4t2a6">
              {formErrors.general}
            </div>}
          {/* Personal Info Section */}
          <div data-id="element-4554" data-mp="10ph1q0kaodxl">
            <div className="flex items-center gap-2 mb-4" data-id="element-4555" data-mp="10p7daheweeu0">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-4556" data-mp="15pv87e68zfti"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4557" data-mp="1aoy5gcfzkhqx">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4558" data-mp="37juipxbjpt55">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4559" data-mp="34f3mja8n8h95">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del paciente" data-id="element-4560" data-mp="2zgtrczmx5gnj" />
                <Select label="Sexo" options={genderOptions} value={formData.gender} onChange={value => setFormData({
                ...formData,
                gender: value as Gender
              })} error={formErrors.gender} data-id="element-4561" data-mp="3ifxuxsvoyi7t" />
              </div>

              <Input label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} placeholder="Dirección completa" data-id="element-4562" data-mp="5z2fkybcuwpb" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-4563" data-mp="kjx2qa8eiq38">
            <div className="flex items-center gap-2 mb-4" data-id="element-4564" data-mp="kjev6hgagpg0">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-4565" data-mp="5vk1axqjof1c"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4566" data-mp="5u927b81nvgk">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4567" data-mp="3t2cm4beohsw6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4568" data-mp="3j2lt6e5rnly7">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-4569" data-mp="3j3dodnvgbhny" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="paciente@email.com" data-id="element-4570" data-mp="lfkf2p5b7m5d" />
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div data-id="element-4571" data-mp="1wwabjgjxkzls">
            <div className="flex items-center gap-2 mb-4" data-id="element-4572" data-mp="1wwtvf2t55y2g">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-4573" data-mp="1wu7qlaxmxwnx"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4574" data-mp="1ry0k9h1buj5g">
                Contacto de Emergencia
              </h3>
            </div>
            <div className="bg-red-50/50 dark:bg-red-900/5 rounded-xl p-4 space-y-4 border border-red-100 dark:border-red-900/20" data-id="element-4575" data-mp="24es2z1rriz31">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4576" data-mp="298w95it7f332">
                <Input label="Nombre Completo" value={formData.emergencyContactName} onChange={e => setFormData({
                ...formData,
                emergencyContactName: e.target.value
              })} placeholder="Nombre del contacto" data-id="element-4577" data-mp="2e4l3n7qd9tsi" />
                <Input label="Parentesco" value={formData.emergencyContactRelationship} onChange={e => setFormData({
                ...formData,
                emergencyContactRelationship: e.target.value
              })} placeholder="Ej: Madre, Esposo" data-id="element-4578" data-mp="1uioocwfq57z7" />
              </div>
              <Input label="Teléfono de Emergencia" value={formData.emergencyContactPhone} onChange={e => setFormData({
              ...formData,
              emergencyContactPhone: e.target.value
            })} error={formErrors.emergencyContactPhone} placeholder="Número de contacto" data-id="element-4579" data-mp="11vbsv94zw3lu" />
            </div>
          </div>

          {/* Insurance Section - Only visible with patients_insurance permission */}
          {canSeeInsurance && <div data-id="element-4580" data-mp="s23cby4w7vtw">
              <div className="flex items-center gap-2 mb-4" data-id="element-4581" data-mp="s1l5k7kltz7q">
                <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-4582" data-mp="4c909iseebap"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-4583" data-mp="3vkwl7xmq7whq">
                  Seguro Médico
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-4584" data-mp="2fskfchf9qtp7">
                <Select label="Seguro Médico" options={insuranceOptions} value={formData.insuranceId} onChange={value => setFormData({
              ...formData,
              insuranceId: value
            })} data-id="element-4585" data-mp="222gv1vzy9a2e" />
              </div>
            </div>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-4586" data-mp="2ktzxa0m5gk3d">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-4587" data-mp="19cqhbo28ca6t">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4588" data-mp="1e9gw882aszqn">
              {selectedPatient ? 'Guardar Cambios' : 'Crear Paciente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Patient View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Expediente del Paciente" size="xl" data-id="element-4589" data-mp="212nztoef41gs">
        {selectedPatient && <div className="space-y-4" data-id="element-4590" data-mp="1r48czxmfbed3">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-teal-500 to-cyan-500 shadow-sm" data-id="element-4591" data-mp="2ookv3ort56j9">
              <div className="px-4 py-4" data-id="element-4592" data-mp="2jrkv9vljx00l">
                <div className="flex items-center gap-3" data-id="element-4593" data-mp="2ezkr1qyg7ky7">
                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl font-bold text-white shadow-sm flex-shrink-0" data-id="element-4594" data-mp="2exzxo4nve81x">
                    {selectedPatient.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4595" data-mp="32om3dpaejg8r">
                    <h3 className="text-lg font-bold text-white capitalize leading-tight" data-id="element-4596" data-mp="3ayb2erdqpd0g">
                      {selectedPatient.name}
                    </h3>
                    <p className="text-sm font-medium text-white/80 capitalize" data-id="element-4597" data-mp="3f0eriitc0g47">
                      {getGenderName(selectedPatient.gender)}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0" data-id="element-4598" data-mp="3oyvn5t37tiwl">
                    {!readOnly && viewMode === 'active' && <>
                        {canEdit('patients') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedPatient)} className="bg-white/20 border-white/30 text-white hover:bg-white/30 backdrop-blur-sm" data-id="element-4599" data-mp="1s1v30cubxf6">
                            <EditIcon className="w-4 h-4 mr-1" data-id="element-4600" data-mp="ggfzvgtthvyd" /> Editar
                          </Button>}
                        {canDelete('patients') && <Button variant="danger" size="sm" onClick={() => openActionConfirm(selectedPatient, 'archive')} className="bg-white/20 border-white/30 text-white hover:bg-red-500/60 backdrop-blur-sm" data-id="element-4601" data-mp="1nb2zrs0hy2ny">
                            <ArchiveIcon className="w-4 h-4" data-id="element-4602" data-mp="261u5mjl9zaxl" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-4603" data-mp="217pwbtojeqtc">
              <TabPanel id="info" activeTab={activeTab} data-id="element-4604" data-mp="2oxtzy11q6qkc">
                <div className="space-y-3" data-id="element-4605" data-mp="2ox2c5ceddx4m">
                  {/* Contact Info - compact rows */}
                  <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700" data-id="element-4606" data-mp="2tqdgn6vpm1td">
                    {/* Phone */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-4607" data-mp="dagz3nugmsxe">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4608" data-mp="rzd4fd54trub">
                        <PhoneIcon className="w-4 h-4 text-blue-500" data-id="element-4609" data-mp="3vgn2w2nzqboa" />
                      </div>
                      <div className="min-w-0" data-id="element-4610" data-mp="3qhjwoew9xzeq">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-4611" data-mp="3qittz1vaz3kb">
                          Teléfono
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4612" data-mp="49ahtcjzle33">
                          {selectedPatient.phone || <span className="text-gray-400 font-normal" data-id="element-4613" data-mp="1fphars9pg5p0">
                              Sin teléfono
                            </span>}
                        </p>
                      </div>
                    </div>
                    {/* Email */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-4614" data-mp="1rtl9eo2bd5w8">
                      <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4615" data-mp="1wp0mhdshyxrl">
                        <MailIcon className="w-4 h-4 text-purple-500" data-id="element-4616" data-mp="142z2x17yjtoc" />
                      </div>
                      <div className="min-w-0" data-id="element-4617" data-mp="190hfd1l6or13">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-4618" data-mp="18z6b70ntth9g">
                          Correo
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4619" data-mp="1iseq678a5gza">
                          {selectedPatient.email || <span className="text-gray-400 font-normal" data-id="element-4620" data-mp="qa9c8jhhi0kw">
                              Sin correo
                            </span>}
                        </p>
                      </div>
                    </div>
                    {/* Address */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-4621" data-mp="2uk4dr2f3pmmp">
                      <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4622" data-mp="2pljhtf6vtp42">
                        <MapPinIcon className="w-4 h-4 text-orange-500" data-id="element-4623" data-mp="2knriw17tl2qd" />
                      </div>
                      <div className="min-w-0" data-id="element-4624" data-mp="2ftx338vc57wk">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-4625" data-mp="2btmv5qnzg7f2">
                          Dirección
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4626" data-mp="21vp8v8pookp3">
                          {(() => {
                        const a = selectedPatient.address;
                        if (!a || typeof a !== 'string') return <span className="text-gray-400 font-normal" data-id="element-4627" data-mp="vtv4o06ecl7">
                                  Sin dirección
                                </span>;
                        const trimmed = a.trim();
                        if (!trimmed || trimmed === '{}' || trimmed === '[]' || trimmed === 'null') return <span className="text-gray-400 font-normal" data-id="element-4628" data-mp="34b9bs8327kfb">
                                  Sin dirección
                                </span>;
                        return trimmed;
                      })()}
                        </p>
                      </div>
                    </div>
                    {/* Insurance */}
                    {canSeeInsurance && <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800" data-id="element-4629" data-mp="2s0i3dwc3bv7x">
                        <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4630" data-mp="2n2ovmyv52t5j">
                          <ShieldIcon className="w-4 h-4 text-teal-500" data-id="element-4631" data-mp="2wzbexj7thc5g" />
                        </div>
                        <div className="min-w-0" data-id="element-4632" data-mp="3aq6hkdrq2x0l">
                          <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" data-id="element-4633" data-mp="3aovcmamjjj7w">
                            Seguro Médico
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4634" data-mp="3ko43uvq0ujqk">
                            {selectedPatient.insuranceId ? getInsurance(selectedPatient.insuranceId)?.name : <span className="text-gray-400 font-normal" data-id="element-4635" data-mp="1lcjt3hl8xw1p">
                                Sin seguro
                              </span>}
                          </p>
                        </div>
                      </div>}
                  </div>

                  {/* Emergency Contact */}
                  <div className="rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 overflow-hidden" data-id="element-4636" data-mp="384xzfd4tbez">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-red-100 dark:border-red-900/30" data-id="element-4637" data-mp="866im4q7hisk">
                      <span className="w-2 h-5 rounded-full bg-red-500 flex-shrink-0" data-id="element-4638" data-mp="d4rjm6da86i2"></span>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-4639" data-mp="hyca7dcjpy78">
                        Contacto de Emergencia
                      </h3>
                    </div>
                    <div className="divide-y divide-red-100 dark:divide-red-900/20" data-id="element-4640" data-mp="qz2060f4kud1">
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-4641" data-mp="vw0vgmaiz7ct">
                        <UsersIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-4642" data-mp="160h8l7wmeacx" />
                        <div className="min-w-0" data-id="element-4643" data-mp="15yeae0lbdax4">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-4644" data-mp="110w82t1sxty3">
                            Nombre
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4645" data-mp="s1hhzupjrle4">
                            {selectedPatient.emergencyContact?.name || <span className="text-gray-400 font-normal" data-id="element-4646" data-mp="1ot2zddso7aqn">
                                No registrado
                              </span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-4647" data-mp="1azbysuwx3172">
                        <FileTextIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-4648" data-mp="27irqswxbc919" />
                        <div className="min-w-0" data-id="element-4649" data-mp="22qhvt2hccis4">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-4650" data-mp="22owxr2vtihpi">
                            Parentesco
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4651" data-mp="2r6jx9zbmbmse">
                            {selectedPatient.emergencyContact?.relationship || <span className="text-gray-400 font-normal" data-id="element-4652" data-mp="2hf5t3e8q1ysx">
                                No registrado
                              </span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3" data-id="element-4653" data-mp="2v7dl6sp291la">
                        <PhoneIcon className="w-4 h-4 text-red-400 flex-shrink-0" data-id="element-4654" data-mp="2v9gafaie2qoy" />
                        <div className="min-w-0" data-id="element-4655" data-mp="304vljup0i8ho">
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-4656" data-mp="1jsglmov92tzo">
                            Teléfono
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4657" data-mp="1oox7fcez8w9l">
                            {selectedPatient.emergencyContact?.phone || <span className="text-gray-400 font-normal" data-id="element-4658" data-mp="1u21nmm2qwgv">
                                No registrado
                              </span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-4659" data-mp="3fa9ggrs7g3ir">
                <div className="space-y-4" data-id="element-4660" data-mp="3f7na2vh1vu24">
                  {getAppointmentsByPatient(selectedPatient.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4661" data-mp="3o9mr3ns2ey52">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-4662" data-mp="30ihb98m4k5ur" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-4663" data-mp="2wiqfeqhqqzjd">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByPatient(selectedPatient.id).map(apt => {
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-4664" data-mp="2a8s8btho128n">
                          <div className="flex items-center gap-4" data-id="element-4665" data-mp="2j743m8th8o7f">
                            <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-4666" data-mp="2eawvc9dlz0mb">
                              {doctor?.name.charAt(0)}
                            </div>
                            <div data-id="element-4667" data-mp="2vkr1dki89trn">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-4668" data-mp="2vkhj6j3fcqwi">
                                {apt.date} - {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4669" data-mp="2mr5n029z6eir">
                                {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-'}{' '}
                                • {service?.name}
                              </p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-4670" data-mp="27tvhranxe4vh">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>

              {/* Paquetes de Sesiones Tab */}
              <TabPanel id="sessions" activeTab={activeTab} data-id="element-4671" data-mp="1f108xogkizo8">
                <div className="space-y-4" data-id="element-4672" data-mp="1a0cip505ymv2">
                  <div className="flex items-center justify-between" data-id="element-4673" data-mp="1a2qe5eyra135">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4674" data-mp="15ztpb6imh8qr">
                      Asigna servicios con cantidad de sesiones (ej: láser) y
                      lleva el registro de cuántas ha usado el paciente.
                    </p>
                    {!readOnly && canEdit('patients') && viewMode === 'active' && <Button size="sm" onClick={openNewPackageForm} data-id="element-4675" data-mp="1ox3df0yw7aec">
                          <PlusIcon className="w-4 h-4 mr-1" data-id="element-4676" data-mp="1jz1r87666uyn" /> Asignar Servicio
                        </Button>}
                  </div>

                  {!selectedPatient.sessionPackages || selectedPatient.sessionPackages.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4677" data-mp="2clmh136zyz03">
                      <LayersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-4678" data-mp="2oyhui07lpt9c" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-4679" data-mp="2twjmxbqx34xh">
                        Este paciente no tiene paquetes de sesiones asignados
                      </p>
                    </div> : <div className="space-y-3" data-id="element-4680" data-mp="32wycwoett87b">
                      {selectedPatient.sessionPackages.map(pkg => {
                  const service = getService(pkg.serviceId);
                  const doctor = pkg.doctorId ? getDoctor(pkg.doctorId) : undefined;
                  const progress = pkg.totalSessions > 0 ? Math.min(100, Math.round(pkg.usedSessions / pkg.totalSessions * 100)) : 0;
                  const isComplete = pkg.usedSessions >= pkg.totalSessions;
                  const isArchived = pkg.status === 'archivado';
                  const isLogExpanded = expandedPackageLogId === pkg.id;
                  return <div key={pkg.id} className={`rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden ${isArchived ? 'opacity-70' : ''}`} data-id="element-4681" data-mp="2u2io44m3xccp">
                            <div className="p-4" data-id="element-4682" data-mp="2z010y638wbpz">
                              <div className="flex items-start justify-between gap-3 mb-3" data-id="element-4683" data-mp="2yz92mni9jzw0">
                                <div className="min-w-0" data-id="element-4684" data-mp="j7v0csz8p2p8">
                                  <div className="flex items-center gap-2 flex-wrap" data-id="element-4685" data-mp="eauuoj6ay1yn">
                                    <h4 className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-4686" data-mp="1w87wkx01ocq8">
                                      {service?.name || 'Servicio eliminado'}
                                    </h4>
                                    <Badge variant={isArchived ? 'default' : isComplete ? 'success' : pkg.status === 'cancelado' ? 'danger' : 'info'} data-id="element-4687" data-mp="1n8bgvkvmuo7b">
                                      {isArchived ? 'Archivado' : isComplete ? 'Completado' : pkg.status === 'cancelado' ? 'Cancelado' : 'En curso'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1" data-id="element-4688" data-mp="33l5ij2vv7wh0">
                                    <StethoscopeIcon className="w-3 h-3" data-id="element-4689" data-mp="2uie5wzr96uns" />{' '}
                                    {doctor ? doctor.name : 'Sin doctor asignado'}
                                  </p>
                                  {pkg.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-4690" data-mp="2fut0gz6wojcp">
                                      {pkg.notes}
                                    </p>}
                                </div>
                                {!readOnly && canEdit('patients') && <div className="flex gap-1.5 flex-shrink-0" data-id="element-4691" data-mp="md3tkewmowu7">
                                    {!isArchived && <Button variant="ghost" size="sm" onClick={() => openEditPackageForm(pkg)} title="Editar paquete" data-id="element-4692" data-mp="vbz2hyr24l17">
                                        <EditIcon className="w-4 h-4" data-id="element-4693" data-mp="7mwl8s0c4u78" />
                                      </Button>}
                                    {!isArchived && canDelete('patients') && <Button variant="ghost" size="sm" onClick={() => confirmArchivePackage(pkg.id)} title="Archivar paquete" data-id="element-4694" data-mp="hdr8nzpbhzu9">
                                        <ArchiveIcon className="w-4 h-4 text-amber-500" data-id="element-4695" data-mp="2newr2qaie9ps" />
                                      </Button>}
                                    {isArchived && <Button variant="ghost" size="sm" onClick={() => handleUnarchivePackage(pkg.id)} title="Restaurar paquete" data-id="element-4696" data-mp="2xb9w2kbi89zu">
                                        <RotateCcwIcon className="w-4 h-4 text-emerald-500" data-id="element-4697" data-mp="3g0ybf52vhhu3" />
                                      </Button>}
                                  </div>}
                              </div>

                              {/* Progress bar */}
                              <div className="mb-3" data-id="element-4698" data-mp="3krtx7k7o5vh4">
                                <div className="flex items-center justify-between mb-1" data-id="element-4699" data-mp="3krbvi828mp1t">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4700" data-mp="3pp3kat7l5v1s">
                                    {pkg.usedSessions} de {pkg.totalSessions}{' '}
                                    sesiones
                                  </span>
                                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400" data-id="element-4701" data-mp="321vjanh6x85k">
                                    {progress}%
                                  </span>
                                </div>
                                <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4702" data-mp="2t17codu4ma2p">
                                  <div className={`h-full rounded-full transition-all ${isArchived ? 'bg-gray-400' : isComplete ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{
                            width: `${progress}%`
                          }} data-id="element-4703" data-mp="2x3txvvwakwqx"></div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between" data-id="element-4704" data-mp="22y2cuzngst4m">
                                {!readOnly && canEdit('patients') && !isArchived && pkg.status !== 'cancelado' && <Button variant="outline" size="sm" disabled={isComplete} onClick={() => handleRegisterSession(pkg.id)} data-id="element-4705" data-mp="2hn70iwd13pk4">
                                      <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-4706" data-mp="ay6do0305txr" />
                                      {isComplete ? 'Sesiones completas' : 'Registrar sesión manual'}
                                    </Button>}
                                {pkg.log.length > 0 && <button type="button" className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline" onClick={() => setExpandedPackageLogId(isLogExpanded ? null : pkg.id)} data-id="element-4707" data-mp="tqr0p0w0t4tn">
                                    {isLogExpanded ? 'Ocultar historial' : `Ver historial (${pkg.log.length})`}
                                  </button>}
                              </div>

                              {/* Log */}
                              {isLogExpanded && pkg.log.length > 0 && <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2" data-id="element-4708" data-mp="2s7ri4cg2n0la">
                                  {pkg.log.slice().reverse().map(entry => <div key={entry.id} className="flex items-center justify-between gap-3 text-sm bg-gray-50 dark:bg-gray-900/30 rounded-lg px-3 py-2" data-id="element-4709" data-mp="cgwm31dc53cp">
                                        <div className="min-w-0" data-id="element-4710" data-mp="mkk3dh20f19g">
                                          <p className="text-gray-700 dark:text-gray-300" data-id="element-4711" data-mp="mmep2xl3uxel">
                                            {formatDate(entry.date)}
                                            {entry.appointmentId ? ' · cita completada' : ' · registro manual'}
                                          </p>
                                          {entry.registeredBy && <p className="text-xs text-gray-400" data-id="element-4712" data-mp="1j9jc0x6ia526">
                                              por {entry.registeredBy}
                                            </p>}
                                        </div>
                                        {!readOnly && canEdit('patients') && !isArchived && <button type="button" title="Deshacer esta sesión" onClick={() => handleUndoSession(pkg.id, entry.id)} className="text-gray-400 hover:text-red-500 flex-shrink-0" data-id="element-4713" data-mp="3ngcn3f4e5mm9">
                                              <MinusCircleIcon className="w-4 h-4" data-id="element-4714" data-mp="2g3aogeolnqdw" />
                                            </button>}
                                      </div>)}
                                </div>}
                            </div>
                          </div>;
                })}
                    </div>}
                </div>
              </TabPanel>

              {/* Registro de Visitas Tab */}
              <TabPanel id="visits" activeTab={activeTab} data-id="element-4715" data-mp="1zl6651lo03hu">
                {(() => {
              const patientVisits = getClinicalHistoriesByPatient(selectedPatient.id).filter(v => getClinicalHistoryStatus(v.id) === 'active').sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              return <div className="space-y-4" data-id="element-4716" data-mp="1bspmimma43gf">
                      {/* Summary Stats */}
                      {patientVisits.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-4717" data-mp="x6aholo0olm2">
                          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30" data-id="element-4718" data-mp="118f9rfytha6l">
                            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-id="element-4719" data-mp="n9xmtkgqk1pq">
                              {patientVisits.length}
                            </p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 font-medium" data-id="element-4720" data-mp="1fycoz6erqpjn">
                              Visitas registradas
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30" data-id="element-4721" data-mp="166yvpgdeulyy">
                            <p className="text-2xl font-bold text-teal-700 dark:text-teal-400" data-id="element-4722" data-mp="1b48kquww0vm9">
                              {new Set(patientVisits.map(v => v.doctorName)).size}
                            </p>
                            <p className="text-xs text-teal-600 dark:text-teal-500 font-medium" data-id="element-4723" data-mp="3jegao06z6ni7">
                              Doctores atendieron
                            </p>
                          </div>
                          <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/30" data-id="element-4724" data-mp="3t3hr1rhfn6wp">
                            <p className="text-sm font-bold text-purple-700 dark:text-purple-400 truncate" data-id="element-4725" data-mp="35ep2ig3541c4">
                              {patientVisits[0] ? formatDate(patientVisits[0].date) : '-'}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-500 font-medium" data-id="element-4726" data-mp="39eh4g9y1398i">
                              Última visita
                            </p>
                          </div>
                        </div>}

                      {patientVisits.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4727" data-mp="18ptahbmtqfng">
                          <ClipboardListIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-4728" data-mp="1hrj9p3my6ivs" />
                          <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-4729" data-mp="1mkcfluu5jx9g">
                            No hay registros de visitas
                          </p>
                          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-4730" data-mp="1wh8gpfhq1h3z">
                            Los historiales clínicos creados para este paciente
                            aparecerán aquí
                          </p>
                        </div> : patientVisits.map((visit, index) => {
                  const isExpanded = expandedVisitId === visit.id;
                  return <div key={visit.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm" data-id="element-4731" data-mp="b5yw126fm3z8" data-mpi={index}>
                              {/* Visit Header - Always visible */}
                              <button onClick={() => setExpandedVisitId(isExpanded ? null : visit.id)} className="w-full text-left p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-4732" data-mp="3lyvsk9grr8g6">
                                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shadow-sm" data-id="element-4733" data-mp="8jfytl6m0gqb">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-4734" data-mp="k35z9bjix1hm">
                                  <div className="flex items-center gap-2 mb-0.5" data-id="element-4735" data-mp="f4cjtdkof0et">
                                    <p className="font-semibold text-gray-900 dark:text-white text-sm" data-id="element-4736" data-mp="admaxqd1kffd">
                                      {formatDate(visit.date)}
                                    </p>
                                    <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-4737" data-mp="1dqyzjbnwgea"></span>
                                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4738" data-mp="3sizezo6168xm">
                                      Dr. {visit.doctorName}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-4739" data-mp="3immjqluu7n0r">
                                    {visit.personalBackground?.consultationReason ? `Motivo: ${visit.personalBackground.consultationReason}` : visit.diagnosis ? `Dx: ${visit.diagnosis.substring(0, 80)}${visit.diagnosis.length > 80 ? '...' : ''}` : 'Sin motivo registrado'}
                                  </p>
                                </div>
                                <div className="flex-shrink-0" data-id="element-4740" data-mp="16c4al27y5880">
                                  {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-4741" data-mp="1adg6tqlbsrp6" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-4742" data-mp="1fbrb7lsuqa00" />}
                                </div>
                              </button>

                              {/* Expanded Content */}
                              {isExpanded && <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/30" data-id="element-4743" data-mp="2vprb4wrp5zn9">
                                  {/* Signos Vitales de esta visita */}
                                  {visit.physicalExam && (visit.physicalExam.bloodPressure || visit.physicalExam.heartRate || visit.physicalExam.bloodSugar || visit.physicalExam.temperature || visit.physicalExam.weight || visit.physicalExam.height) && <div className="rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/30 p-3" data-id="element-4744" data-mp="1x30hqhf17sf1">
                                        <div className="flex items-center gap-2 mb-2" data-id="element-4745" data-mp="14lxtiy9to8ez">
                                          <ActivityIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" data-id="element-4746" data-mp="14jl2zwq00pqb" />
                                          <h4 className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wider" data-id="element-4747" data-mp="3ba6mqp9tagdp">
                                            Exploración Física
                                          </h4>
                                        </div>
                                        <div className="flex flex-wrap gap-3" data-id="element-4748" data-mp="36b3e6uumhs0z">
                                          {visit.physicalExam.bloodPressure && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4749" data-mp="3u17fuwo93hpc">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4750" data-mp="3kz7yg39bqblv">
                                                P.A.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4751" data-mp="29jjdfhfmrmnp">
                                                {visit.physicalExam.bloodPressure}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.heartRate && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4752" data-mp="3cr3uo0wu9hzf">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4753" data-mp="32th9wxsuyw7j">
                                                F.C.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4754" data-mp="1rgn9c3i6hs2s">
                                                {visit.physicalExam.heartRate}{' '}
                                                bpm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.bloodSugar && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4755" data-mp="gn4hqhxu0kbh">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4756" data-mp="qkirxzt5wcvc">
                                                Glucosa
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4757" data-mp="zkfdhsk98vm6">
                                                {visit.physicalExam.bloodSugar}{' '}
                                                mg/dL
                                              </span>
                                            </div>}
                                          {visit.physicalExam.temperature && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4758" data-mp="hjjlrwl7utym">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4759" data-mp="rgxqiyxs1ub3">
                                                Temp.
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4760" data-mp="108pyn2zbwrci">
                                                {visit.physicalExam.temperature}{' '}
                                                °C
                                              </span>
                                            </div>}
                                          {visit.physicalExam.weight && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4761" data-mp="1tuab6nqygtlz">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4762" data-mp="1jvkugyaspf7b">
                                                Peso
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4763" data-mp="1av4q7mm8a41e">
                                                {visit.physicalExam.weight} kg
                                              </span>
                                            </div>}
                                          {visit.physicalExam.height && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4764" data-mp="r5ubaaub3n4c">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4765" data-mp="12n20bi6hogai">
                                                Altura
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4766" data-mp="soci1oaokttq">
                                                {visit.physicalExam.height} cm
                                              </span>
                                            </div>}
                                          {visit.physicalExam.lungs && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4767" data-mp="a0h6mb2jjs3s">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4768" data-mp="yhrjob36y3h">
                                                Pulmones
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4769" data-mp="2gozmbfod9p3g">
                                                {visit.physicalExam.lungs}
                                              </span>
                                            </div>}
                                          {visit.physicalExam.abdomen && <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-md px-2.5 py-1.5 border border-teal-100 dark:border-teal-800/30" data-id="element-4770" data-mp="22vi5w3qz8shn">
                                              <span className="text-[10px] font-semibold text-gray-400 uppercase" data-id="element-4771" data-mp="27o1jv1tktpmz">
                                                Abdomen
                                              </span>
                                              <span className="text-sm font-bold text-gray-800 dark:text-gray-200" data-id="element-4772" data-mp="ow9w3rpznml1">
                                                {visit.physicalExam.abdomen}
                                              </span>
                                            </div>}
                                        </div>
                                      </div>}

                                  {/* Diagnóstico */}
                                  {visit.diagnosis && <div data-id="element-4773" data-mp="ay229so911e3">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4774" data-mp="3sdiag1lic4b0">
                                        <div className="w-6 h-6 rounded bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center" data-id="element-4775" data-mp="15wd0qpdsy2l">
                                          <StethoscopeIcon className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" data-id="element-4776" data-mp="3jcke85hb912z" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4777" data-mp="34otw9e3pqz4u">
                                          Diagnóstico
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4778" data-mp="39oga9prd8jep">
                                        {visit.diagnosis}
                                      </p>
                                    </div>}

                                  {/* Tratamiento */}
                                  {visit.treatment && <div data-id="element-4779" data-mp="3sf66wwqcf9z4">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4780" data-mp="3sfg3521zedd9">
                                        <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center" data-id="element-4781" data-mp="aa30n8on4rx4">
                                          <PillIcon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" data-id="element-4782" data-mp="58dmvla1a05c" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4783" data-mp="3422ptpund6e6">
                                          Tratamiento
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4784" data-mp="3hth97xsgv9lr">
                                        {visit.treatment}
                                      </p>
                                    </div>}

                                  {/* Indicaciones Médicas */}
                                  {visit.medicalIndications && <div data-id="element-4785" data-mp="1xe5s95otc71x">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4786" data-mp="21eg0yq3zpbkh">
                                        <div className="w-6 h-6 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center" data-id="element-4787" data-mp="21g9969u943ul">
                                          <FileTextIcon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" data-id="element-4788" data-mp="1p3yftu6olff3" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4789" data-mp="1k2r81v4jgg8f">
                                          Indicaciones Médicas
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4790" data-mp="27sljc897lwqk">
                                        {visit.medicalIndications}
                                      </p>
                                    </div>}

                                  {/* Sesiones */}
                                  {(visit.sessions || visit.sessionNumber) && <div data-id="element-4791" data-mp="2bb0x3e1bhw5u">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4792" data-mp="2b9zmtbs4ldp6">
                                        <div className="w-6 h-6 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center" data-id="element-4793" data-mp="2g311pjsc1d8r">
                                          <CalendarIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" data-id="element-4794" data-mp="26cfux92i92er" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4795" data-mp="2u2jwzc01ou3n">
                                          Sesiones
                                        </h4>
                                      </div>
                                      <div className="pl-8 flex gap-4" data-id="element-4796" data-mp="2pzdvsirsht41">
                                        {visit.sessions && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4797" data-mp="19kvvbyz91332">
                                            <span className="font-medium text-gray-500" data-id="element-4798" data-mp="1ej72g1y5szhl">
                                              Sesiones:
                                            </span>{' '}
                                            {visit.sessions}
                                          </p>}
                                        {visit.sessionNumber && <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-4799" data-mp="1b1jfako2urfe">
                                            <span className="font-medium text-gray-500" data-id="element-4800" data-mp="nbfd8hqjezqi">
                                              N° Sesión:
                                            </span>{' '}
                                            {visit.sessionNumber}
                                          </p>}
                                      </div>
                                    </div>}

                                  {/* Comentarios */}
                                  {visit.comments && <div data-id="element-4801" data-mp="1zoccv26eso5k">
                                      <div className="flex items-center gap-2 mb-1.5" data-id="element-4802" data-mp="24p06lvm0vj3h">
                                        <div className="w-6 h-6 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center" data-id="element-4803" data-mp="29gi6shg81fne">
                                          <FileTextIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" data-id="element-4804" data-mp="2dgjxxwobyqof" />
                                        </div>
                                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4805" data-mp="1lqdz9yzwwvr4">
                                          Comentarios
                                        </h4>
                                      </div>
                                      <p className="text-sm text-gray-700 dark:text-gray-300 pl-8 whitespace-pre-wrap" data-id="element-4806" data-mp="1uozilwd7kesy">
                                        {visit.comments}
                                      </p>
                                    </div>}

                                  {/* Firma */}
                                  {visit.doctorSignature && <div className="pt-3 border-t border-gray-200 dark:border-gray-700 pl-8" data-id="element-4807" data-mp="36kml10zkqpug">
                                      <p className="text-xs text-gray-400" data-id="element-4808" data-mp="32jar4j9o3igg">
                                        Firma:{' '}
                                        <span className="font-medium text-gray-600 dark:text-gray-300" data-id="element-4809" data-mp="2xkzha8jdh7y8">
                                          {visit.doctorSignature}
                                        </span>
                                      </p>
                                    </div>}
                                </div>}
                            </div>;
                })}
                    </div>;
            })()}
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Modal: Asignar / Editar Paquete de Sesiones */}
      <Modal isOpen={isPackageFormOpen} onClose={() => setIsPackageFormOpen(false)} title={editingPackageId ? 'Editar Paquete de Sesiones' : 'Asignar Servicio con Sesiones'} size="md" icon={<LayersIcon className="w-5 h-5" data-id="element-4811" data-mp="hd2tc1z1218u" />} accentColor="blue" data-id="element-4810" data-mp="151vkll4pfkx4">
        <form onSubmit={handlePackageSubmit} className="space-y-4" data-id="element-4812" data-mp="mcnumj1o6loq">
          {!editingPackageId && <Select label="Servicio" options={activeServiceOptions} value={packageFormData.serviceId} onChange={value => setPackageFormData({
          ...packageFormData,
          serviceId: value
        })} error={packageFormErrors.serviceId} placeholder="Selecciona un servicio (ej: Láser facial)" data-id="element-4813" data-mp="8d5z7ktvca6e" />}
          {editingPackageId && <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-4814" data-mp="2cv45qa31v49g">
              Servicio:{' '}
              <span className="font-medium text-gray-900 dark:text-white" data-id="element-4815" data-mp="2qntq6a2xk14g">
                {getService(packageFormData.serviceId)?.name}
              </span>
            </div>}
          <Select label="Doctor asignado" options={doctorOptionsForPackages} value={packageFormData.doctorId} onChange={value => setPackageFormData({
          ...packageFormData,
          doctorId: value
        })} placeholder="Selecciona un doctor (opcional)" disabled={isDoctorUser} data-id="element-4816" data-mp="30j57n05q3mfw" />
          <Input type="number" label="Cantidad total de sesiones" value={packageFormData.totalSessions} onChange={e => setPackageFormData({
          ...packageFormData,
          totalSessions: parseInt(e.target.value) || 0
        })} error={packageFormErrors.totalSessions} min={1} placeholder="Ej: 6" data-id="element-4817" data-mp="1f7dlnbswz4wd" />
          <Textarea label="Notas (opcional)" value={packageFormData.notes} onChange={e => setPackageFormData({
          ...packageFormData,
          notes: e.target.value
        })} rows={2} placeholder="Ej: Paquete promocional, no requiere todas las sesiones..." data-id="element-4818" data-mp="2h88wj03tub7k" />
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-4819" data-mp="307ej25o7d8u9">
            <Button variant="outline" type="button" onClick={() => setIsPackageFormOpen(false)} data-id="element-4820" data-mp="3nwper8qp4d1j">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-4821" data-mp="1h8gj7b38hszn">
              {editingPackageId ? 'Guardar Cambios' : 'Asignar Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Pacientes" isExporting={isExporting} data-id="element-4822" data-mp="1qz1gmv768rgz" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Pacientes a PDF" isExporting={isPdfExporting} data-id="element-4823" data-mp="ybxjfceblhfr" />
    </div>;
}